/*
  Lavish reels, 2026-10-18. Her clips play muted while they are in view and stop when they
  leave it; nothing downloads until a clip is near the screen. A tap opens the viewer: the
  clip larger and with sound, the product underneath with its Add button (handled by
  lavish-aov.js like every other one-tap add), arrows, swipe and the keyboard to move
  between clips. Reduced motion or data saver: no autoplay, the clip plays when tapped.
  Opening a clip publishes `lavish_reel_open` to Shopify's customer events.
*/
const pad = (n) => String(n).padStart(2, '0');

class LavishReels extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-reel]')];
    this.track = this.querySelector('[data-reel-track]');
    this.viewer = this.querySelector('[data-reel-viewer]');
    this.big = this.querySelector('[data-viewer-video]');
    this.count = this.querySelector('[data-viewer-count]');
    this.productSlot = this.querySelector('[data-viewer-product]');
    this.soundButton = this.querySelector('[data-viewer-sound]');
    this.sound = true;
    this.index = 0;
    this.visible = new Set();

    const quiet =
      window.matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData === true;
    this.autoplay = this.dataset.autoplay === 'true' && !quiet;

    this.observeCards();
    this.setUpArrows();

    this.addEventListener('click', (event) => this.onClick(event));
    this.viewer.addEventListener('click', (event) => {
      if (event.target === this.viewer) this.close();
    });
    this.viewer.addEventListener('close', () => this.onClosed());
    this.viewer.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.step(1);
      if (event.key === 'ArrowLeft') this.step(-1);
    });
    this.setUpSwipe();
    this.productSlot.addEventListener('click', (event) => this.closeAfterAdd(event));

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAll();
      else if (!this.viewer.open) this.playVisible();
    });
    window.addEventListener('pageshow', () => {
      if (!this.viewer.open) this.playVisible();
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  /* Cards: load near the screen, play while at least 60% visible. */
  observeCards() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const card = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            this.visible.add(card);
            if (!this.viewer.open) this.playCard(card);
          } else {
            this.visible.delete(card);
            this.pauseCard(card);
          }
        }
      },
      { threshold: [0, 0.6] }
    );
    this.cards.forEach((card) => this.observer.observe(card));
  }

  playCard(card) {
    if (!this.autoplay || document.hidden) return;
    const video = card.querySelector('video');
    if (!video || !video.dataset.src) return;
    if (!video.getAttribute('src')) video.src = video.dataset.src;
    video
      .play()
      .then(() => card.classList.add('is-playing'))
      .catch(() => card.classList.remove('is-playing'));
  }

  pauseCard(card) {
    card.querySelector('video')?.pause();
    card.classList.remove('is-playing');
  }

  pauseAll() {
    this.cards.forEach((card) => this.pauseCard(card));
    this.big.pause();
  }

  playVisible() {
    this.visible.forEach((card) => this.playCard(card));
  }

  /* Arrows in the head scroll the row by a screenful; shown only when the row overflows. */
  setUpArrows() {
    const arrows = this.querySelector('[data-reel-arrows]');
    if (!arrows || !this.track) return;
    const update = () => {
      arrows.hidden = this.track.scrollWidth <= this.track.clientWidth + 2;
    };
    update();
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(this.track);
    }
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const scroller = target.closest('[data-reel-scroll]');
    if (scroller) {
      const direction = Number(scroller.dataset.reelScroll);
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
      return;
    }

    const opener = target.closest('[data-reel-open]');
    if (opener) {
      this.open(this.cards.indexOf(opener.closest('[data-reel]')));
      return;
    }

    const stepper = target.closest('[data-viewer-step]');
    if (stepper) {
      this.step(Number(stepper.dataset.viewerStep));
      return;
    }

    if (target.closest('[data-viewer-close]')) {
      this.close();
      return;
    }

    if (target.closest('[data-viewer-sound]')) {
      if (!this.hasSound) return;
      this.sound = !this.sound;
      this.big.muted = !this.sound;
      this.syncSound();
      if (this.sound) this.big.play().catch(() => {});
    }
  }

  /* The viewer. */
  open(index) {
    if (index < 0 || !this.cards[index]?.querySelector('video')) return;
    this.pauseAll();
    this.viewer.showModal();
    this.show(index);
    const handle = this.cards[index].querySelector('[data-reel-product] a')?.getAttribute('href');
    window.Shopify?.analytics?.publish?.('lavish_reel_open', { position: index + 1, product: handle || null });
  }

  show(index) {
    const playable = this.cards.filter((card) => card.querySelector('video'));
    const total = playable.length;
    const card = this.cards[index];
    const position = playable.indexOf(card);
    this.index = index;

    const source = card.querySelector('video');
    this.hasSound = source.dataset.sound === 'true';
    this.soundButton.hidden = !this.hasSound;
    this.big.poster = source.poster || '';
    this.big.src = source.dataset.srcLarge || source.dataset.src;
    this.big.muted = !this.hasSound || !this.sound;
    this.syncSound();
    this.big.play().catch(() => {
      // A browser that refuses sound without a fresh gesture still plays the clip, muted.
      if (this.big.muted) return;
      this.sound = false;
      this.big.muted = true;
      this.syncSound();
      this.big.play().catch(() => {});
    });

    this.count.textContent = `${pad(position + 1)} / ${pad(total)}`;
    const product = card.querySelector('[data-reel-product]');
    this.productSlot.replaceChildren(...(product ? [product.cloneNode(true)] : []));
    this.productSlot.hidden = !product;
    this.viewer.querySelectorAll('[data-viewer-step]').forEach((button) => {
      button.hidden = total < 2;
    });
  }

  step(direction) {
    const playable = this.cards.filter((card) => card.querySelector('video'));
    if (playable.length < 2) return;
    const position = playable.indexOf(this.cards[this.index]);
    const next = playable[(position + direction + playable.length) % playable.length];
    this.show(this.cards.indexOf(next));
  }

  close() {
    if (this.viewer.open) this.viewer.close();
  }

  onClosed() {
    this.big.pause();
    this.big.removeAttribute('src');
    this.big.load();
    this.playVisible();
  }

  syncSound() {
    this.soundButton.setAttribute('aria-pressed', String(this.sound));
    this.soundButton.setAttribute('aria-label', this.sound ? 'Mute' : 'Sound on');
    this.soundButton.classList.toggle('is-muted', !this.sound);
  }

  /* Swipe sideways to move between clips, down to close. */
  setUpSwipe() {
    const stage = this.querySelector('[data-viewer-stage]');
    let x = 0;
    let y = 0;
    stage.addEventListener(
      'touchstart',
      (event) => {
        x = event.touches[0].clientX;
        y = event.touches[0].clientY;
      },
      { passive: true }
    );
    stage.addEventListener(
      'touchend',
      (event) => {
        const dx = event.changedTouches[0].clientX - x;
        const dy = event.changedTouches[0].clientY - y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.step(dx < 0 ? 1 : -1);
        else if (dy > 80 && dy > Math.abs(dx)) this.close();
      },
      { passive: true }
    );
  }

  /* After a successful add from the viewer, step aside so the cart drawer is in view. */
  closeAfterAdd(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-lavish-add]') : null;
    if (!button) return;
    const watcher = new MutationObserver(() => {
      if (button.hasAttribute('aria-busy')) return;
      watcher.disconnect();
      if (button.disabled) setTimeout(() => this.close(), 700);
    });
    watcher.observe(button, { attributes: true, attributeFilter: ['aria-busy'] });
  }
}

if (!customElements.get('lavish-reels')) customElements.define('lavish-reels', LavishReels);
